/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { Grid } from '@material-ui/core';
import ReactHTMLParser from 'react-html-parser';
import mbpLogger from 'mbp-logger';
import {
    string, shape, number, object, func, bool, arrayOf,
} from 'prop-types';
import { transformCharacters } from '../../../../../helpers/markdown/characterTransformer';
import GraphqlFoodQuickViewContainer from './GraphqlFoodQuickView/GraphqlFoodQuickViewContainer';
import YotpoProductReview from '../YotpoProductReview';
import ProductReview from '../ProductReview';
import { getBaseCode } from '../../../../../helpers/tracking/product/dataLayerProductHelpers';
import { productType, selectedSkuType } from '../common/types';
import { dateStringReformat } from '../common/commonProductPageHelper';
import { CrossMerch } from '../../../CrossMerchContainer/CrossMerchContainer';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useIsDisplayChannel from '../../../../../helpers/hooks/useIsDisplayChannel';

const FoodRadioLabel = ({
    classes,
    product,
    isPassportBundleProduct,
    productSku,
    location,
    productUrl,
    brand,
    selectedSku,
    isPdpYotpoEnabled,
    renderSkuPrice,
    skuType,
    pricesList,
    isCyoProduct,
    isPdpRedesignEnabled,
}) => {
    if (!productSku) return null;

    const { featureFlags = {} } = useContext(CrossMerch);
    const isDisplayChannel = useIsDisplayChannel();
    const isPlaPage = useIsPlaPage();
    const foodPlaRedesign = useSelector(getFeatureFlag('which-food-pla-design'));
    const plaThreeColumn = (isPlaPage || isDisplayChannel) && foodPlaRedesign === 'three_column';
    const {
        name,
        partNumber,
        shortDescription,
        image,
    } = product || {};
    useEffect(() => {
        if (!isCyoProduct && typeof window !== 'undefined' && typeof window.yotpo !== 'undefined' && typeof window.yotpo.refreshWidgets === 'function') {
            try {
                // Call refresh only if yotpo initialized
                if (window.yotpo.initialized) {
                    window.yotpo.refreshWidgets();
                }
            } catch (exception) {
                mbpLogger.logError({ appName: process.env.npm_package_name, message: 'Unable to refresh the yotpo widget', jsError: exception });
            }
        }
    }, [isCyoProduct]);

    const availabilityMessage = (sku) => {
        const { availability } = sku || {};
        const deliveryMessage = availability?.deliveryMessage || (sku.__typename !== 'Upsell' ? product.availability?.deliveryMessage : '' || '');
        const availToShipMessage = deliveryMessage.replace('AVAILABLE FOR DELIVERY', 'AVAILABLE TO SHIP');
        const dateObject = dateStringReformat(availability?.earliestDefaultShipDate || availability?.earliestShipDate, true) || {};
        const earliestShipDate = new Date(dateObject?.year, dateObject?.month, dateObject?.day);
        const currentDate = new Date();
        if (earliestShipDate > currentDate) {
            return <div className={classes.productAvailibilityMessage} data-testid="earliestDefaultShipDate">{featureFlags['is-delivery-message-next-variant'] ? 'NEXT AVAILABLE DATE' : 'Available to Ship'}: {format(earliestShipDate, 'LLL do yyyy')}</div>;
        }
        if (deliveryMessage === 'AVAILABLE TO SHIP NOW') {
            return <div className={classes.availabletoShip}>{deliveryMessage}</div>;
        }
        return <div className={classes.productAvailibilityMessage} data-testid="availToShipMessage">{availToShipMessage}</div>;
    };
    const itemSku = (id) => id.match(/[^-_]*$/);

    let productName = productSku.name;
    if (isPassportBundleProduct?.enable) {
        if (productName?.length < 30) {
            if (isPassportBundleProduct?.skuText) {
                productName = `${productName} ${isPassportBundleProduct?.skuText}`;
            } else {
                productName += 'Bundle';
            }
        }
    }

    // Ensure the name is not updated unnecessarily
    const [transformedName, setTransformedName] = useState(productName);

    useEffect(() => {
        // The name styles kept glitching on hover, this ensures these functions are only called when the name changes
        setTransformedName(transformCharacters(ReactHTMLParser(productName)));
    }, [productName]);

    const isPmallProduct = featureFlags['is-pmall-pdp-template-enabled'] && (product?.brand === 'personalizationmall' || product?.brand === 'thingsremembered');
    if (isPmallProduct) {
        return (
            <Grid container className={classes.listItem} data-testid="foodRadioLabel-Container">
                <Grid item xs={9} sm={9} lg={9}>
                    <Grid item sm={12} lg={12} className={classes.pmallSkuLabelName}>
                        <img className={classes.skuImage} src={`${productSku?.image?.path}${productSku?.image?.name}x.jpg?auto=webp`} alt={productSku?.image?.name} />
                        {transformedName}
                    </Grid>
                </Grid>
                <Grid item xs={3} sm={3} lg={3} className={classes.pmallProductPrice}>
                    {renderSkuPrice(productSku, false, pricesList)}
                </Grid>
            </Grid>
        );
    }

    return (
        isPdpRedesignEnabled ? (
            <Grid container className={classes.pdpListItem} data-testid="foodRadioLabel-Container">
                <Grid item xs={9}>
                    <Grid item sm={12} lg={12} className={classes.skuLabelName}>{transformedName}</Grid>
                </Grid>
                <Grid item xs={3}  className={classes.selctedSkuLabelAmount}>
                    {renderSkuPrice(productSku, false, pricesList)}
                </Grid>
            </Grid>
        ) : (
            <Grid container className={classes.listItem} data-testid="foodRadioLabel-Container">
                <Grid item xs={6} sm={plaThreeColumn ? 6 : 7} lg={plaThreeColumn ? 6 : 7}>
                    <Grid item sm={12} lg={12} className={classes.skuLabelName}>{transformedName}</Grid>
                    {featureFlags['is-available-to-ship-now']
                    && (
                        <Grid item sm={12} lg={12} className={classes.availableToShipText}>
                            {availabilityMessage(productSku)}
                        </Grid>
                    )}
                    <Grid item>
                        {
                        // eslint-disable-next-line no-underscore-dangle
                            skuType === 'Upsell'
                                ? (
                                    <GraphqlFoodQuickViewContainer
                                        classes={classes}
                                        product={product}
                                        selectedSku={productSku}
                                        location={location}
                                        productUrl={productUrl}
                                        brand={brand}
                                        renderSkuPrice={renderSkuPrice}
                                        onClickTracking={{
                                            eventCategory: 'PDP Upsell',
                                            eventAction: 'Default Upsell Quick View',
                                            eventLabel: productSku?.id || '',
                                        }}
                                    />
                                ) : null
                        }
                    </Grid>
                </Grid>
                <Grid item xs={2} sm={2} lg={2} className={classes.skuLabelProdId} aria-hidden="true">{itemSku(productSku.id)}</Grid>
                <Grid item xs={4} sm={plaThreeColumn ? 4 : 3} lg={plaThreeColumn ? 4 : 3}>
                    {!isCyoProduct && selectedSku?.id === productSku.id && featureFlags['is-pdp-reviews-enabled'] && featureFlags['is-power-review-enabled'] && (
                        <Grid item sm={12} lg={12}>
                            <ProductReview
                                right
                                productUrl={productUrl}
                                name={name}
                                partNumber={getBaseCode(partNumber)}
                                shortDescription={shortDescription}
                                image={image}
                                selectedSku={selectedSku}
                            />
                        </Grid>
                    )}
                    { !isCyoProduct && selectedSku?.id === productSku.id && featureFlags['is-pdp-reviews-enabled'] && featureFlags['is-pdp-yotpo-enabled'] && (
                        <Grid item sm={12} lg={12}>
                            <YotpoProductReview
                                right
                                productUrl={productUrl}
                                name={name}
                                partNumber={getBaseCode(partNumber)}
                                shortDescription={shortDescription}
                                image={image}
                                selectedSku={selectedSku}
                            />
                        </Grid>
                    )}
                    <Grid item sm={12} lg={12} className={selectedSku?.id !== productSku.id ? classes.skuLabelAmount : classes.selctedSkuLabelAmount}>
                        {renderSkuPrice(productSku, false, pricesList)}
                    </Grid>
                </Grid>
            </Grid>
        )

    );
};

FoodRadioLabel.propTypes = {
    classes: object.isRequired,
    skuType: string.isRequired,
    brand: shape({
        domain: string,
    }).isRequired,
    selectedSku: selectedSkuType.isRequired,
    product: productType.isRequired,
    productUrl: string.isRequired,
    location: shape({
        state: shape({
            categoryId: string,
            categoryName: string,
        }),
    }).isRequired,
    renderSkuPrice: func,
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
    productSku: selectedSkuType.isRequired,
    isPdpYotpoEnabled: bool,
    pricesList: arrayOf(shape({
        value: number,
        type: string,
    })),
    isCyoProduct: bool,
    isPdpRedesignEnabled: bool,
};

FoodRadioLabel.defaultProps = {
    isPassportBundleProduct: {},
    isPdpYotpoEnabled: false,
    renderSkuPrice: () => { },
    pricesList: null,
    isCyoProduct: false,
    isPdpRedesignEnabled: false,
};

export default (FoodRadioLabel);
